import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { asyncPopulateUsersAndThreads } from '../states/shared/action';
import { setCategoryActionCreator } from '../states/category/action';
import { toggleLikeThread, toggleDislikeThread, isVoted } from '../utils/vote';
import { convertStringToTitleCase } from '../utils/string';
import { useSearch } from '../hooks/useSearch';
import Search from '../components/Search';
import MarkdownParser from '../components/utilities/MarkdownParser';
import OwnerAndTime from '../components/utilities/OwnerAndTime';
import CategoryName from '../components/utilities/CategoryName';
import Button from '../components/utilities/Button';
import FilterButton from '../components/utilities/FilterButton';
import LikeDislikeButton from '../components/utilities/LikeDislikeButton';
import CommentCount from '../components/utilities/CommentCount';
import HastagIcon from '../components/icons/HastagIcon';

export default function ThreadsPage() {
  const {
    threads = [],
    users = [],
    authUser,
    category: stateCategory,
  } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [keyword, onSearch] = useSearch();
  const [threadList, setThreadList] = useState([]);
  const [sort, setSort] = useState('newest');

  const threadsCategory = [
    ...new Set(threads?.map(({ category }) => category)),
  ];

  const handleFilterCategory = (category) => {
    dispatch(setCategoryActionCreator(category));
  };

  useMemo(
    (type) => {
      if (sort === 'oldest') {
        threadList?.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      } else if (sort === 'newest') {
        threadList?.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } else if (sort === 'top') {
        threadList?.sort((a, b) => b.upVotesBy.length - a.upVotesBy.length);
      }
    },
    [sort, threadList]
  );

  useEffect(() => {
    dispatch(asyncPopulateUsersAndThreads());
  }, [dispatch]);

  useEffect(() => {
    const filteredThreads = threads?.filter(
      (thread) => !stateCategory || thread?.category === stateCategory
    );

    const getThreadWithUser = filteredThreads?.map((thread) => ({
      ...thread,
      user: users?.find(({ id }) => id === thread.ownerId),
    }));

    if (keyword) {
      const filtered = getThreadWithUser?.filter(({ title }) =>
        title.toLowerCase().includes(keyword)
      );
      setThreadList(filtered);
    } else {
      setThreadList(getThreadWithUser);
    }
  }, [keyword, threads, users, stateCategory]);

  return (
    <section>
      {threads?.length && users?.length ? (
        <>
          <div className="w-full flex flex-col md:flex-row justify-between items-end md:items-center gap-4 mb-10">
            <Search keyword={keyword} onSearch={onSearch} />
            <div className="flex flex-col md:flex-row items-end md:items-center gap-4">
              <div className="flex">
                <FilterButton
                  label="Oldest"
                  isStart
                  onClick={() => setSort('oldest')}
                  isActive={sort === 'oldest'}
                />
                <FilterButton
                  label="Newest"
                  isEnd
                  isActive={sort === 'newest'}
                  onClick={() => setSort('newest')}
                />
              </div>
              {authUser && (
                <Link to="/add-new-thread">
                  <Button label="Add New Thread" />
                </Link>
              )}
            </div>
          </div>
          <div className="w-full flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/4">
              <p className="mb-4 uppercase tracking-wider text-lg font-extrabold text-zinc-500">
                Categories
              </p>
              <ul className="flex flex-wrap lg:block overflow-auto max-h-32 lg:max-h-96">
                <CategoryName
                  name="All"
                  isActive={stateCategory === ''}
                  onFilter={() => handleFilterCategory('')}
                />
                {threadsCategory?.map((category) => (
                  <CategoryName
                    key={category}
                    name={convertStringToTitleCase(category)}
                    isActive={stateCategory === category}
                    onFilter={() => handleFilterCategory(category)}
                  />
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-3/4">
              <h1 className="font-extrabold text-4xl">Threads</h1>
              <ul>
                {threadList?.length ? (
                  threadList.map((thread) => (
                    <li
                      key={thread.id}
                      className="flex flex-col gap-y-3 mt-8 rounded-lg bg-zinc-100 dark:bg-zinc-900/50 px-6 py-4">
                      <div className="text-zinc-500 dark:text-zinc-400 font-semibold flex gap-x-2 items-center -mb-1">
                        <HastagIcon />
                        {thread.category}
                      </div>
                      <Link
                        to={`/detail/${thread.id}`}
                        className="text-2xl font-medium lg:font-semibold hover:text-cyan-500 dark:hover:text-cyan-600 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-cyan-500 dark:focus-visible:outline-cyan-600 rounded-md">
                        {thread.title}
                      </Link>
                      <MarkdownParser
                        content={thread?.body}
                        className="line-clamp-2"
                      />
                      <OwnerAndTime
                        owner={thread?.user?.name}
                        avatar={thread?.user?.avatar}
                        createdAt={thread.createdAt}
                        wrapperClass="mt-2"
                      />
                      <div className="flex gap-x-6 mt-3">
                        <LikeDislikeButton
                          likeCount={thread?.upVotesBy?.length}
                          dislikeCount={thread?.downVotesBy?.length}
                          liked={isVoted(thread?.upVotesBy, authUser?.id)}
                          disliked={isVoted(thread?.downVotesBy, authUser?.id)}
                          onToggleLike={() =>
                            toggleLikeThread({
                              voteData: thread?.upVotesBy,
                              threadId: thread?.id,
                              userId: authUser?.id,
                            })
                          }
                          onToggleDislike={() =>
                            toggleDislikeThread({
                              voteData: thread?.downVotesBy,
                              threadId: thread?.id,
                              userId: authUser?.id,
                            })
                          }
                        />
                        <CommentCount count={thread?.totalComments} />
                      </div>
                    </li>
                  ))
                ) : (
                  <p className="mt-8">No threads.</p>
                )}
              </ul>
            </div>
          </div>
        </>
      ) : (
        <div>Loading</div>
      )}
    </section>
  );
}
